// Display helpers shared by the console, admin, and public platform pages.

const BYTES_PER_MB = 1024 ** 2;
const BYTES_PER_GB = 1024 ** 3;

export function formatMoney(cents: number | null | undefined, currency = 'usd'): string {
  const amount = (cents ?? 0) / 100;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount);
}

// A whole-dollar price for plan cards: $39, not $39.00.
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

// Turns what a person typed ("1,840.50") into cents. Null when it is not a number.
export function parseMoney(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, '');

  if (cleaned === '' || Number.isNaN(Number(cleaned))) return null;

  return Math.round(Number(cleaned) * 100);
}

export function centsToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';

  return (cents / 100).toFixed(2);
}

export function formatBytes(bytes: number | null | undefined): string {
  const value = bytes ?? 0;

  if (value >= BYTES_PER_GB) return `${(value / BYTES_PER_GB).toFixed(1)} GB`;
  if (value >= BYTES_PER_MB) return `${(value / BYTES_PER_MB).toFixed(1)} MB`;
  if (value >= 1024) return `${Math.round(value / 1024)} KB`;

  return `${value} B`;
}

export function formatNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-US').format(value ?? 0);
}

// A date-only string ("2026-10-05") is shown as that day everywhere, not shifted by time zone.
export function formatDate(value: string | null | undefined): string {
  if (!value) return '';

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly
    ? new Date(Date.UTC(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])))
    : new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: dateOnly ? 'UTC' : undefined,
  }).format(date);
}

// The year is left out for a date in the current year and shown for any other,
// so a date years away is not read as this year's.
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function humanize(value: string | null | undefined): string {
  const text = (value ?? '').replace(/_/g, ' ');

  return text.charAt(0).toUpperCase() + text.slice(1);
}
